import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import useStore from '@/store/useStore';
import { toast } from '@/components/ui/use-toast';
import { X, ChevronDown, Pencil } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Team } from '@/types/cricket';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { getRandomColor } from '@/store/useStore';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { getPlayerById } from '@/services/playerService';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Loader2 } from 'lucide-react';

const playerRoles = [
  { id: 'batsman', label: 'Batsman' },
  { id: 'bowler', label: 'Bowler' },
  { id: 'all-rounder', label: 'All-Rounder' },
  { id: 'wicket-keeper', label: 'Wicket-Keeper' },
];

const NewPlayerPage = () => {
  const navigate = useNavigate();
  const { teams, fetchTeams, addPlayer } = useStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [role, setRole] = useState<'' | 'batsman' | 'bowler' | 'all-rounder' | 'wicket-keeper'>('batsman');
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [profilePicturePreviewUrl, setProfilePicturePreviewUrl] = useState<string | null>(null);
  const profilePicFileInputRef = useRef<HTMLInputElement>(null);
  
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreviewUrl, setCoverImagePreviewUrl] = useState<string | null>(null);
  const coverImageFileInputRef = useRef<HTMLInputElement>(null);
  
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [croppingFor, setCroppingFor] = useState<'profile' | 'cover' | null>(null);
  
  // Role-specific fields
  const [battingStyle, setBattingStyle] = useState<'left-handed' | 'right-handed'>('right-handed');
  const [battingOrder, setBattingOrder] = useState<'opener' | 'top-order' | 'middle-order' | 'lower-order'>('middle-order');
  const [bowlingStyle, setBowlingStyle] = useState<'fast' | 'medium' | 'spin'>('medium');
  const [bowlingArm, setBowlingArm] = useState<'left-arm' | 'right-arm'>('right-arm');
  const [bowlingType, setBowlingType] = useState<'pace' | 'off-spin' | 'leg-spin' | 'left-arm-orthodox' | 'left-arm-unorthodox'>('pace');
  const [keepingStyle, setKeepingStyle] = useState<'conventional' | 'aggressive'>('conventional');
  const [fieldingPosition, setFieldingPosition] = useState('');
  const [fieldingStyle, setFieldingStyle] = useState<'aggressive' | 'balanced' | 'defensive'>('balanced');
  
  // Bio Data fields
  const [showBioData, setShowBioData] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [nationality, setNationality] = useState('');
  const [heightCm, setHeightCm] = useState<number | ''>('');
  const [weightKg, setWeightKg] = useState<number | ''>('');
  const [jerseyNumber, setJerseyNumber] = useState<number | ''>('');
  const [nickname, setNickname] = useState('');
  
  // Add new state for Gender
  const [gender, setGender] = useState<'male' | 'female' | 'other' | ''>('');
  
  // Cricket Performance & Health Data fields
  const [showCricketData, setShowCricketData] = useState(false);
  const [injuryStatus, setInjuryStatus] = useState<'fit' | 'injured' | 'recovering'>('fit');
  const [matchFitness, setMatchFitness] = useState<number | ''>('');
  const [recentForm, setRecentForm] = useState('');
  const [careerRuns, setCareerRuns] = useState<number | ''>('');
  const [careerMatchesPlayed, setCareerMatchesPlayed] = useState<number | ''>('');
  const [careerInningsPlayed, setCareerInningsPlayed] = useState<number | ''>('');
  const [careerStrikeRate, setCareerStrikeRate] = useState<number | ''>('');
  const [careerAverage, setCareerAverage] = useState<number | ''>('');
  const [highestScore, setHighestScore] = useState<number | ''>('');
  const [notOuts, setNotOuts] = useState<number | ''>('');
  const [fours, setFours] = useState<number | ''>('');
  const [sixes, setSixes] = useState<number | ''>('');
  const [careerWickets, setCareerWickets] = useState<number | ''>('');
  const [careerOversBowled, setCareerOversBowled] = useState<number | ''>('');
  const [careerEconomyRate, setCareerEconomyRate] = useState<number | ''>('');
  const [careerBowlingAverage, setCareerBowlingAverage] = useState<number | ''>('');
  const [bestBowlingFigures, setBestBowlingFigures] = useState('');
  const [maidenOvers, setMaidenOvers] = useState<number | ''>('');
  const [catches, setCatches] = useState<number | ''>('');
  const [runOuts, setRunOuts] = useState<number | ''>('');
  const [stumpings, setStumpings] = useState<number | ''>('');
  
  const [existingPlayerInput, setExistingPlayerInput] = useState('');
  const [isAddingExisting, setIsAddingExisting] = useState(false);
  const [previewPlayers, setPreviewPlayers] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErrors, setPreviewErrors] = useState<Record<string, string>>({});
  
  const getCroppedImage = useCallback(async (imageSrc: string, crop: Area, rotation = 0): Promise<string> => {
    const image = new Image();
    image.src = imageSrc;

    const promise = new Promise((resolve, reject) => {
      image.onload = () => resolve(image);
      image.onerror = (err) => reject(err);
    });

    const loadedImage = await promise as HTMLImageElement;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2D context');
    }

    const rotationInRadians = (rotation * Math.PI) / 180;

    // calculate canvas size after rotation
    const originalWidth = loadedImage.naturalWidth;
    const originalHeight = loadedImage.naturalHeight;

    const sines = Math.abs(Math.sin(rotationInRadians));
    const cosines = Math.abs(Math.cos(rotationInRadians));

    const rotatedWidth = originalWidth * cosines + originalHeight * sines;
    const rotatedHeight = originalWidth * sines + originalHeight * cosines;

    canvas.width = rotatedWidth;
    canvas.height = rotatedHeight;

    ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
    ctx.rotate(rotationInRadians);
    ctx.drawImage(loadedImage, -originalWidth / 2, -originalHeight / 2, originalWidth, originalHeight);

    const data = ctx.getImageData(
      rotatedWidth / 2 - crop.width / 2,
      rotatedHeight / 2 - crop.height / 2,
      crop.width,
      crop.height
    );

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.clearRect(0, 0, crop.width, crop.height);
    ctx.putImageData(data, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        resolve(URL.createObjectURL(blob));
      }, 'image/jpeg');
    });
  }, []);

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'cover') => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (type === 'profile') {
        setProfilePictureFile(file);
        setProfilePicturePreviewUrl(URL.createObjectURL(file));
      } else {
        setCoverImageFile(file);
        setCoverImagePreviewUrl(URL.createObjectURL(file));
      }
      setCroppingFor(type);
      setIsCropModalOpen(true);
    }
  };

  const handleCropConfirm = useCallback(async () => {
    let currentPreviewUrl: string | null = null;
    let currentFile: File | null = null;
    let setFile: React.Dispatch<React.SetStateAction<File | null>>;
    let setPreviewUrl: React.Dispatch<React.SetStateAction<string | null>>;

    if (croppingFor === 'profile') {
      currentPreviewUrl = profilePicturePreviewUrl;
      currentFile = profilePictureFile;
      setFile = setProfilePictureFile;
      setPreviewUrl = setProfilePicturePreviewUrl;
    } else if (croppingFor === 'cover') {
      currentPreviewUrl = coverImagePreviewUrl;
      currentFile = coverImageFile;
      setFile = setCoverImageFile;
      setPreviewUrl = setCoverImagePreviewUrl;
    }

    if (currentPreviewUrl && croppedAreaPixels && currentFile) {
      const croppedImageUrl = await getCroppedImage(currentPreviewUrl, croppedAreaPixels, rotation);

      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const croppedFileName = `${croppingFor}_${currentFile.name}` || `${croppingFor}_image.jpeg`;
      const croppedFile = new File([blob], croppedFileName, {
        type: currentFile?.type || 'image/jpeg',
      });
      setFile(croppedFile);

      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
      setPreviewUrl(croppedImageUrl);
      setIsCropModalOpen(false);
      setCroppingFor(null);
    }
  }, [profilePicturePreviewUrl, croppedAreaPixels, rotation, getCroppedImage, profilePictureFile, croppingFor, coverImagePreviewUrl, coverImageFile]);

  const handleCropCancel = useCallback(() => {
    setIsCropModalOpen(false);
    setCroppingFor(null);
    if (croppingFor === 'profile') {
      setProfilePictureFile(null);
      if (profilePicturePreviewUrl) {
        URL.revokeObjectURL(profilePicturePreviewUrl);
      }
      setProfilePicturePreviewUrl(null);
    } else if (croppingFor === 'cover') {
      setCoverImageFile(null);
      if (coverImagePreviewUrl) {
        URL.revokeObjectURL(coverImagePreviewUrl);
      }
      setCoverImagePreviewUrl(null);
    }
  }, [croppingFor, profilePicturePreviewUrl, coverImagePreviewUrl]);

  // Fetch teams when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchTeams();
      } catch (error) {
        console.error('Error loading teams:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [fetchTeams]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation for required fields
    if (!lastName.trim() || !firstName.trim() || selectedTeams.length === 0) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields and select at least one team.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      await addPlayer({
        name: `${lastName}${middleName ? ' ' + middleName : ''} ${firstName}`.trim(),
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        role,
        teamIds: selectedTeams.map(team => team.id),
        battingStyle: role === 'batsman' || role === 'all-rounder' ? battingStyle : undefined,
        battingOrder: role === 'batsman' || role === 'all-rounder' ? battingOrder : undefined,
        bowlingStyle: role === 'bowler' || role === 'all-rounder' ? bowlingStyle : undefined,
        bowlingArm: role === 'bowler' || role === 'all-rounder' ? bowlingArm : undefined,
        bowlingType: role === 'bowler' || role === 'all-rounder' ? bowlingType : undefined,
        keepingStyle: role === 'wicket-keeper' ? keepingStyle : undefined,
        fieldingPosition,
        fieldingStyle,
        injuryStatus: injuryStatus || undefined,
        matchFitness: typeof matchFitness === 'number' ? matchFitness : undefined,
        recentForm: recentForm || undefined,
        careerRuns: typeof careerRuns === 'number' ? careerRuns : undefined,
        careerMatchesPlayed: typeof careerMatchesPlayed === 'number' ? careerMatchesPlayed : undefined,
        careerInningsPlayed: typeof careerInningsPlayed === 'number' ? careerInningsPlayed : undefined,
        careerStrikeRate: typeof careerStrikeRate === 'number' ? careerStrikeRate : undefined,
        careerAverage: typeof careerAverage === 'number' ? careerAverage : undefined,
        highestScore: typeof highestScore === 'number' ? highestScore : undefined,
        notOuts: typeof notOuts === 'number' ? notOuts : undefined,
        fours: typeof fours === 'number' ? fours : undefined,
        sixes: typeof sixes === 'number' ? sixes : undefined,
        careerWickets: typeof careerWickets === 'number' ? careerWickets : undefined,
        careerOversBowled: typeof careerOversBowled === 'number' ? careerOversBowled : undefined,
        careerEconomyRate: typeof careerEconomyRate === 'number' ? careerEconomyRate : undefined,
        careerBowlingAverage: typeof careerBowlingAverage === 'number' ? careerBowlingAverage : undefined,
        bestBowlingFigures: bestBowlingFigures || undefined,
        maidenOvers: typeof maidenOvers === 'number' ? maidenOvers : undefined,
        catches: typeof catches === 'number' ? catches : undefined,
        runOuts: typeof runOuts === 'number' ? runOuts : undefined,
        stumpings: typeof stumpings === 'number' ? stumpings : undefined,
        dateOfBirth: dateOfBirth || undefined,
        placeOfBirth: placeOfBirth || undefined,
        nationality: nationality || undefined,
        heightCm: typeof heightCm === 'number' ? heightCm : undefined,
        weightKg: typeof weightKg === 'number' ? weightKg : undefined,
        jerseyNumber: typeof jerseyNumber === 'number' ? jerseyNumber : undefined,
        nickname: nickname || undefined,
        gender: gender || undefined,
        profilePictureFile: profilePictureFile,
        coverImageFile: coverImageFile,
        logoColor: getRandomColor(),
      });
      toast({
        title: "Success",
        description: "Player added successfully!",
      });
      navigate('/players');
    } catch (error) {
      console.error('Error adding player:', error);
      toast({
        title: "Error",
        description: "Failed to add player.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTeamSelect = (team: Team) => {
    if (!selectedTeams.find(t => t.id === team.id)) {
      setSelectedTeams([...selectedTeams, team]);
    }
    setTeamSearchQuery('');
  };

  const handleTeamRemove = (teamId: string) => {
    setSelectedTeams(selectedTeams.filter(team => team.id !== teamId));
  };

  const filteredTeams = teams.filter(team => 
    !selectedTeams.find(t => t.id === team.id) &&
    team.name.toLowerCase().includes(teamSearchQuery.toLowerCase())
  );

  // Redirect if no teams exist
  if (!isLoading && teams.length === 0) {
    toast({
      title: "Cannot add players yet",
      description: "You need to create a team first.",
      variant: "destructive",
    });
    navigate('/teams/new');
    return null;
  }

  // Helper to extract ID from input (URL or direct ID)
  function extractPlayerId(input: string): string | null {
    if (!input) return null;
    // If input is a URL, try to extract the last segment as ID
    try {
      const url = new URL(input);
      const segments = url.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || null;
    } catch {
      // Not a URL, treat as direct ID
      return input;
    }
  }

  // Parse textarea input into unique, non-empty IDs
  function parsePlayerInputs(input: string): string[] {
    return Array.from(
      new Set(
        input
          .split(/[\n,\s]+/)
          .map(s => s.trim())
          .filter(Boolean)
      )
    );
  }

  // Fetch player preview when input changes
  useEffect(() => {
    const ids = parsePlayerInputs(existingPlayerInput);
    if (ids.length === 0) {
      setPreviewPlayers([]);
      setPreviewErrors({});
      return;
    }
    setPreviewLoading(true);
    setPreviewErrors({});
    Promise.all(
      ids.map(async (raw) => {
        const id = extractPlayerId(raw);
        if (!id) return { id: raw, error: 'Invalid input' };
        try {
          const player = await getPlayerById(id);
          return { ...player, input: raw };
        } catch {
          return { id: raw, error: 'Player not found', input: raw };
        }
      })
    ).then(results => {
      setPreviewPlayers(results.filter(p => !p.error));
      const errors: Record<string, string> = {};
      results.forEach(p => { if (p.error) errors[p.input || p.id] = p.error; });
      setPreviewErrors(errors);
    }).finally(() => setPreviewLoading(false));
  }, [existingPlayerInput]);

  // Handler for adding existing player (stub)
  const handleAddExistingPlayer = (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddingExisting(true);
    // TODO: Implement actual add logic for all previewPlayers
    setTimeout(() => {
      toast({ title: 'Players added (stub)', description: `${previewPlayers.length} player(s) added.` });
      setIsAddingExisting(false);
      setExistingPlayerInput('');
      setPreviewPlayers([]);
      setPreviewErrors({});
    }, 1000);
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <Button 
        variant="ghost" 
        onClick={() => navigate("/players")} 
        className="mb-4"
      >
        ← Back to Players
      </Button>
      <Tabs defaultValue="new" className="w-full">
        <Card className="p-6">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-2xl">Add New Player</CardTitle>
            <TabsList className="mb-0">
              <TabsTrigger value="new">Add New Player</TabsTrigger>
              <TabsTrigger value="existing">Add Existing Player</TabsTrigger>
            </TabsList>
          </CardHeader>
          <TabsContent value="new">
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Player Name */}
                <div className="space-y-2">
                  <Label>Player Name</Label>
                  <div className="flex flex-col md:flex-row gap-2">
                    <div className="flex-1">
                      <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Last Name"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="flex-1">
                      <Label htmlFor="middleName">Middle Name</Label>
                      <Input
                        id="middleName"
                        type="text"
                        placeholder="Middle Name"
                        value={middleName}
                        onChange={e => setMiddleName(e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="First Name"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Profile Picture and Cover Image - in the same line */}
                <div className="flex flex-col md:flex-row gap-4 items-start w-full">
                  {/* Profile Picture Upload */}
                  <div className="flex flex-col items-center justify-center flex-1">
                    <input
                      type="file"
                      ref={profilePicFileInputRef}
                      onChange={(e) => handleFileChange(e, 'profile')}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                    <Label className="mb-2">Profile Picture</Label>
                    {!profilePicturePreviewUrl ? (
                      <div
                        className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer overflow-hidden relative group"
                        onClick={() => profilePicFileInputRef.current?.click()}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-12 h-12 text-gray-500"
                        >
                          <path
                            fillRule="evenodd"
                            d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18a.75.75 0 0 0 .75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.69a1.5 1.5 0 0 0-2.12 0L9.47 17.13a3 3 0 0 1-4.24 0L3 16.06ZM14.25 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Pencil className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="w-24 h-24 rounded-full overflow-hidden relative group cursor-pointer"
                        onClick={() => {
                          setCroppingFor('profile');
                          setIsCropModalOpen(true);
                        }}
                      >
                        <img
                          key={profilePicturePreviewUrl}
                          src={profilePicturePreviewUrl}
                          alt="Profile Picture Preview"
                          className="w-full h-full object-cover rounded-full"
                          onError={(e) => {
                            console.error("Error loading image:", e);
                            e.currentTarget.src = "https://via.placeholder.com/96"; // Fallback image
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Pencil className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cover Image Upload */}
                  <div className="flex flex-col items-center justify-center flex-1">
                    <input
                      type="file"
                      ref={coverImageFileInputRef}
                      onChange={(e) => handleFileChange(e, 'cover')}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                    <Label className="mb-2">Cover Image</Label>
                    {!coverImagePreviewUrl ? (
                      <div
                        className="w-full h-24 bg-gray-200 flex items-center justify-center cursor-pointer overflow-hidden relative group"
                        onClick={() => coverImageFileInputRef.current?.click()}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-12 h-12 text-gray-500"
                        >
                          <path
                            fillRule="evenodd"
                            d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18a.75.75 0 0 0 .75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.69a1.5 1.5 0 0 0-2.12 0L9.47 17.13a3 3 0 0 1-4.24 0L3 16.06ZM14.25 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Pencil className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="w-full h-24 overflow-hidden relative group cursor-pointer"
                        onClick={() => {
                          setCroppingFor('cover');
                          setIsCropModalOpen(true);
                        }}
                      >
                        <img
                          key={coverImagePreviewUrl}
                          src={coverImagePreviewUrl}
                          alt="Cover Image Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error("Error loading image:", e);
                            e.currentTarget.src = "https://via.placeholder.com/256x96"; // Fallback image updated to match new height
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Pencil className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Dialog open={isCropModalOpen} onOpenChange={setIsCropModalOpen}>
                  <DialogContent className="sm:max-w-[425px] h-[500px] flex flex-col">
                    <DialogHeader>
                      <DialogTitle>Crop {croppingFor === 'profile' ? 'Profile Picture' : 'Cover Image'}</DialogTitle>
                      <DialogDescription>
                        Adjust the crop, zoom, and rotation of your {croppingFor === 'profile' ? 'profile picture' : 'cover image'}.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="relative w-full h-64 bg-gray-100 flex-grow">
                      {(croppingFor === 'profile' && profilePicturePreviewUrl) || (croppingFor === 'cover' && coverImagePreviewUrl) ? (
                        <Cropper
                          image={croppingFor === 'profile' ? profilePicturePreviewUrl : coverImagePreviewUrl}
                          crop={crop}
                          zoom={zoom}
                          rotation={rotation}
                          aspect={croppingFor === 'profile' ? 1 : 16 / 9} // 1 for profile, 16/9 for cover
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onRotationChange={setRotation}
                          onCropComplete={onCropComplete}
                          cropShape={croppingFor === 'profile' ? 'round' : 'rect'}
                        />
                      ) : null}
                    </div>
                    <div className="space-y-4 pt-4">
                      <div className="flex items-center space-x-2">
                        <Label className="w-16">Zoom</Label>
                        <Slider
                          min={1}
                          max={3}
                          step={0.1}
                          value={[zoom]}
                          onValueChange={(val) => setZoom(val[0])}
                          className="flex-grow"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label className="w-16">Rotation</Label>
                        <Slider
                          min={0}
                          max={360}
                          step={1}
                          value={[rotation]}
                          onValueChange={(val) => setRotation(val[0])}
                          className="flex-grow"
                        />
                      </div>
                    </div>
                    <DialogFooter className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={handleCropCancel}>
                        Cancel
                      </Button>
                      <Button onClick={handleCropConfirm}>
                        OK
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <div className="space-y-2">
                  <Label>Teams <span className="text-red-500">*</span></Label>
                  <div className="space-y-4">
                    {/* Selected Teams Display */}
                    <div className="flex flex-wrap gap-2">
                      {selectedTeams.map(team => (
                        <div
                          key={team.id}
                          className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full"
                        >
                          <span>{team.name}</span>
                          <button
                            type="button"
                            onClick={() => handleTeamRemove(team.id)}
                            className="hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Team Search and Selection */}
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Search teams..."
                        value={teamSearchQuery}
                        onChange={(e) => setTeamSearchQuery(e.target.value)}
                        className="w-full"
                      />
                      {teamSearchQuery && filteredTeams.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg">
                          {filteredTeams.map(team => (
                            <button
                              key={team.id}
                              type="button"
                              onClick={() => handleTeamSelect(team)}
                              className="w-full px-4 py-2 text-left hover:bg-accent"
                            >
                              {team.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Player Role <span className="text-red-500">*</span></Label>
                  <RadioGroup 
                    defaultValue="batsman" 
                    value={role}
                    onValueChange={(value) => setRole(value as '' | 'batsman' | 'bowler' | 'all-rounder' | 'wicket-keeper')}
                    className="grid grid-cols-2 gap-4"
                  >
                    {playerRoles.map(playerRole => (
                      <div key={playerRole.id} className="flex items-center space-x-2">
                        <RadioGroupItem value={playerRole.id} id={`role-${playerRole.id}`} />
                        <Label htmlFor={`role-${playerRole.id}`}>{playerRole.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Role-specific fields */}
                {(role === 'batsman' || role === 'all-rounder') && (
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="font-semibold">Batting Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Batting Style</Label>
                        <Select value={battingStyle} onValueChange={(value) => setBattingStyle(value as 'left-handed' | 'right-handed')}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select batting style" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left-handed">Left-handed</SelectItem>
                            <SelectItem value="right-handed">Right-handed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Batting Order</Label>
                        <Select value={battingOrder} onValueChange={(value) => setBattingOrder(value as 'opener' | 'top-order' | 'middle-order' | 'lower-order')}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select batting order" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="opener">Opener</SelectItem>
                            <SelectItem value="top-order">Top Order</SelectItem>
                            <SelectItem value="middle-order">Middle Order</SelectItem>
                            <SelectItem value="lower-order">Lower Order</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {(role === 'bowler' || role === 'all-rounder') && (
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="font-semibold">Bowling Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Bowling Style</Label>
                        <Select value={bowlingStyle} onValueChange={(value) => setBowlingStyle(value as 'fast' | 'medium' | 'spin')}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select bowling style" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fast">Fast</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="spin">Spin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Bowling Arm</Label>
                        <Select value={bowlingArm} onValueChange={(value) => setBowlingArm(value as 'left-arm' | 'right-arm')}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select bowling arm" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left-arm">Left Arm</SelectItem>
                            <SelectItem value="right-arm">Right Arm</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Bowling Type</Label>
                        <Select value={bowlingType} onValueChange={(value) => setBowlingType(value as 'pace' | 'off-spin' | 'leg-spin' | 'left-arm-orthodox' | 'left-arm-unorthodox')}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select bowling type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pace">Pace</SelectItem>
                            <SelectItem value="off-spin">Off Spin</SelectItem>
                            <SelectItem value="leg-spin">Leg Spin</SelectItem>
                            <SelectItem value="left-arm-orthodox">Left Arm Orthodox</SelectItem>
                            <SelectItem value="left-arm-unorthodox">Left Arm Unorthodox</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {role === 'wicket-keeper' && (
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="font-semibold">Wicket-keeping Details</h3>
                    <div className="space-y-2">
                      <Label>Keeping Style</Label>
                      <Select value={keepingStyle} onValueChange={(value) => setKeepingStyle(value as 'conventional' | 'aggressive')}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select keeping style" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="conventional">Conventional</SelectItem>
                          <SelectItem value="aggressive">Aggressive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Common fields for all roles */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold">Fielding Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fielding Position</Label>
                      <Input 
                        value={fieldingPosition}
                        onChange={(e) => setFieldingPosition(e.target.value)}
                        placeholder="Enter preferred fielding position"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fielding Style</Label>
                      <Select value={fieldingStyle} onValueChange={(value) => setFieldingStyle(value as 'aggressive' | 'balanced' | 'defensive')}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select fielding style" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aggressive">Aggressive</SelectItem>
                          <SelectItem value="balanced">Balanced</SelectItem>
                          <SelectItem value="defensive">Defensive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Optional Cricket Performance & Health Data Section */}
                <Collapsible open={showCricketData} onOpenChange={setShowCricketData} className="space-y-4 border-t pt-4">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between pr-4">
                      <h3 className="font-semibold">Optional Cricket Performance & Health Data</h3>
                      <ChevronDown className={`h-4 w-4 transition-transform ${showCricketData ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="injuryStatus">Injury Status</Label>
                        <Select value={injuryStatus} onValueChange={(value) => setInjuryStatus(value as 'fit' | 'injured' | 'recovering')}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fit">Fit</SelectItem>
                            <SelectItem value="injured">Injured</SelectItem>
                            <SelectItem value="recovering">Recovering</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="matchFitness">Match Fitness (%)</Label>
                        <Input 
                          id="matchFitness"
                          type="number"
                          value={matchFitness}
                          onChange={(e) => setMatchFitness(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 95"
                          min={0}
                          max={100}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="recentForm">Recent Form</Label>
                        <Input 
                          id="recentForm"
                          value={recentForm}
                          onChange={(e) => setRecentForm(e.target.value)}
                          placeholder="e.g., Good, Average, Poor"
                        />
                      </div>
                    </div>
                    
                    <h4 className="font-semibold mt-4">Batting Performance</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="careerRuns">Career Runs</Label>
                        <Input 
                          id="careerRuns"
                          type="number"
                          value={careerRuns}
                          onChange={(e) => setCareerRuns(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 5000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="careerMatchesPlayed">Matches Played</Label>
                        <Input 
                          id="careerMatchesPlayed"
                          type="number"
                          value={careerMatchesPlayed}
                          onChange={(e) => setCareerMatchesPlayed(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 100"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="careerInningsPlayed">Innings Played</Label>
                        <Input 
                          id="careerInningsPlayed"
                          type="number"
                          value={careerInningsPlayed}
                          onChange={(e) => setCareerInningsPlayed(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 90"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="careerStrikeRate">Strike Rate</Label>
                        <Input 
                          id="careerStrikeRate"
                          type="number"
                          step="0.01"
                          value={careerStrikeRate}
                          onChange={(e) => setCareerStrikeRate(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 120.50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="careerAverage">Average</Label>
                        <Input 
                          id="careerAverage"
                          type="number"
                          step="0.01"
                          value={careerAverage}
                          onChange={(e) => setCareerAverage(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 35.75"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="highestScore">Highest Score</Label>
                        <Input 
                          id="highestScore"
                          type="number"
                          value={highestScore}
                          onChange={(e) => setHighestScore(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 150"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="notOuts">Not Outs</Label>
                        <Input 
                          id="notOuts"
                          type="number"
                          value={notOuts}
                          onChange={(e) => setNotOuts(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 15"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="fours">Fours</Label>
                        <Input 
                          id="fours"
                          type="number"
                          value={fours}
                          onChange={(e) => setFours(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 450"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sixes">Sixes</Label>
                        <Input 
                          id="sixes"
                          type="number"
                          value={sixes}
                          onChange={(e) => setSixes(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 100"
                        />
                      </div>
                    </div>

                    <h4 className="font-semibold mt-4">Bowling Performance</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="careerWickets">Career Wickets</Label>
                        <Input 
                          id="careerWickets"
                          type="number"
                          value={careerWickets}
                          onChange={(e) => setCareerWickets(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="careerOversBowled">Career Overs Bowled</Label>
                        <Input 
                          id="careerOversBowled"
                          type="number"
                          step="0.1"
                          value={careerOversBowled}
                          onChange={(e) => setCareerOversBowled(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 1500.3"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="careerEconomyRate">Economy Rate</Label>
                        <Input 
                          id="careerEconomyRate"
                          type="number"
                          step="0.01"
                          value={careerEconomyRate}
                          onChange={(e) => setCareerEconomyRate(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 5.25"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="careerBowlingAverage">Bowling Average</Label>
                        <Input 
                          id="careerBowlingAverage"
                          type="number"
                          step="0.01"
                          value={careerBowlingAverage}
                          onChange={(e) => setCareerBowlingAverage(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 25.50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bestBowlingFigures">Best Bowling Figures</Label>
                        <Input 
                          id="bestBowlingFigures"
                          value={bestBowlingFigures}
                          onChange={(e) => setBestBowlingFigures(e.target.value)}
                          placeholder="e.g., 5/25"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="maidenOvers">Maiden Overs</Label>
                        <Input 
                          id="maidenOvers"
                          type="number"
                          value={maidenOvers}
                          onChange={(e) => setMaidenOvers(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 10"
                        />
                      </div>
                    </div>

                    <h4 className="font-semibold mt-4">Fielding Performance</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="catches">Catches</Label>
                        <Input 
                          id="catches"
                          type="number"
                          value={catches}
                          onChange={(e) => setCatches(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="runOuts">Run Outs</Label>
                        <Input 
                          id="runOuts"
                          type="number"
                          value={runOuts}
                          onChange={(e) => setRunOuts(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stumpings">Stumpings</Label>
                        <Input 
                          id="stumpings"
                          type="number"
                          value={stumpings}
                          onChange={(e) => setStumpings(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 5"
                        />
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Optional Bio Data Section */}
                <Collapsible open={showBioData} onOpenChange={setShowBioData} className="space-y-4 border-t pt-4">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between pr-4">
                      <h3 className="font-semibold">Bio Data(Optional)</h3>
                      <ChevronDown className={`h-4 w-4 transition-transform ${showBioData ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="dateOfBirth">Date of Birth</Label>
                        <Input 
                          id="dateOfBirth"
                          type="date"
                          value={dateOfBirth}
                          onChange={(e) => setDateOfBirth(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="placeOfBirth">Place of Birth</Label>
                        <Input 
                          id="placeOfBirth"
                          value={placeOfBirth}
                          onChange={(e) => setPlaceOfBirth(e.target.value)}
                          placeholder="e.g., London, England"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="nationality">Nationality</Label>
                        <Input 
                          id="nationality"
                          value={nationality}
                          onChange={(e) => setNationality(e.target.value)}
                          placeholder="e.g., Australian"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="heightCm">Height (cm)</Label>
                        <Input 
                          id="heightCm"
                          type="number"
                          value={heightCm}
                          onChange={(e) => setHeightCm(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 180"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="weightKg">Weight (kg)</Label>
                        <Input 
                          id="weightKg"
                          type="number"
                          value={weightKg}
                          onChange={(e) => setWeightKg(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 75"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="jerseyNumber">Jersey Number</Label>
                        <Input 
                          id="jerseyNumber"
                          type="number"
                          value={jerseyNumber}
                          onChange={(e) => setJerseyNumber(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="nickname">Nickname</Label>
                        <Input 
                          id="nickname"
                          value={nickname}
                          onChange={(e) => setNickname(e.target.value)}
                          placeholder="e.g., Punter"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gender">Gender</Label>
                        <Select value={gender} onValueChange={(value) => setGender(value as 'male' | 'female' | 'other')}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                
                <Button 
                  type="submit" 
                  className="bg-cricket-green w-full" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Player'}
                </Button>
              </form>
            </CardContent>
          </TabsContent>
          <TabsContent value="existing">
            <CardContent>
              <form onSubmit={handleAddExistingPlayer} className="space-y-4">
                <Label htmlFor="existing-player-input">Player Profile URLs, Unique IDs, or Player IDs (one per line, comma, or space)</Label>
                <textarea
                  id="existing-player-input"
                  value={existingPlayerInput}
                  onChange={e => setExistingPlayerInput(e.target.value)}
                  placeholder="Paste player profile URLs, unique IDs, or player IDs (one per line, comma, or space)"
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  required
                />
                {previewLoading && (
                  <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin h-5 w-5" /> Loading...</div>
                )}
                {Object.keys(previewErrors).length > 0 && !previewLoading && (
                  <div className="text-destructive text-sm">
                    {Object.entries(previewErrors).map(([input, error]) => (
                      <div key={input}>{input}: {error}</div>
                    ))}
                  </div>
                )}
                {previewPlayers.length > 0 && !previewLoading && (
                  <div className="space-y-2 mt-2">
                    {previewPlayers.map(player => (
                      <div key={player.id} className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                        <Avatar className="h-12 w-12">
                          {player.avatarUrl ? (
                            <AvatarImage src={player.avatarUrl} alt={player.name} />
                          ) : (
                            <AvatarFallback>{player.name?.[0] || '?'}</AvatarFallback>
                          )}
                        </Avatar>
                        <div>
                          <div className="font-semibold text-lg">{player.name}</div>
                          {player.role && <div className="text-muted-foreground text-sm">{player.role}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button type="submit" disabled={isAddingExisting || previewPlayers.length === 0}>
                  {isAddingExisting ? 'Adding...' : `Add ${previewPlayers.length > 1 ? `${previewPlayers.length} Players` : 'Player'}`}
                </Button>
              </form>
            </CardContent>
          </TabsContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default NewPlayerPage;
